import React, { useEffect, useRef, useState } from 'react';
import { Mail, Heart } from 'lucide-react';
import NewsletterModal from './NewsletterModal';

const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT;
const DONATION_URL = process.env.REACT_APP_DONATION_URL;

// Per-variant configuration. Slot IDs are pasted later via env vars.
const SLOT_CONFIG = {
  leaderboard: {
    slot: process.env.REACT_APP_ADSENSE_SLOT_LEADERBOARD,
    format: 'horizontal',
    minHeight: 90,
    wrapper: 'w-full max-w-5xl mx-auto',
  },
  'in-article': {
    slot: process.env.REACT_APP_ADSENSE_SLOT_INARTICLE,
    format: 'rectangle',
    minHeight: 250,
    wrapper: 'w-full max-w-md mx-auto',
  },
  sidebar: {
    slot: process.env.REACT_APP_ADSENSE_SLOT_SIDEBAR,
    format: 'vertical',
    minHeight: 600,
    wrapper: 'w-full',
  },
};

// Inject the AdSense library once, only when a publisher ID is configured.
let adScriptRequested = false;
const ensureAdScript = (client) => {
  if (!client || adScriptRequested) return;
  if (document.querySelector('script[data-adsense="true"]')) {
    adScriptRequested = true;
    return;
  }
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`;
  s.crossOrigin = 'anonymous';
  s.setAttribute('data-adsense', 'true');
  s.onerror = () => { /* ad blocker / network — slots fall back automatically */ };
  document.head.appendChild(s);
  adScriptRequested = true;
};

const AdSlot = ({ variant = 'leaderboard', className = '' }) => {
  const cfg = SLOT_CONFIG[variant] || SLOT_CONFIG.leaderboard;
  const insRef = useRef(null);
  const [showFallback, setShowFallback] = useState(!ADSENSE_CLIENT);
  const [newsletterOpen, setNewsletterOpen] = useState(false);

  useEffect(() => {
    if (!ADSENSE_CLIENT) {
      setShowFallback(true);
      return;
    }
    ensureAdScript(ADSENSE_CLIENT);
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      setShowFallback(true);
      return;
    }
    // After a grace period, detect ad-blocker / unfilled inventory and fall back.
    const timer = setTimeout(() => {
      const el = insRef.current;
      const status = el && el.getAttribute('data-ad-status');
      if (!window.adsbygoogle || !el || status === 'unfilled' || el.offsetHeight < 10) {
        setShowFallback(true);
      }
    }, 2500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Fallback: subtle newsletter / support prompt ----
  if (showFallback) {
    return (
      <>
        <div
          data-testid={`ad-fallback-${variant}`}
          className={`${cfg.wrapper} ${className} flex flex-col items-center justify-center gap-2 text-center rounded-xl border-2 border-dashed border-black/20 dark:border-white/20 bg-[#FFFDF5] dark:bg-[#2D2A26] px-4 py-5`}
          style={{ minHeight: variant === 'sidebar' ? 320 : 100 }}
        >
          <p className="text-sm font-bold dark:text-white" style={{ fontFamily: 'Merriweather, serif' }}>
            Enjoying Daily Wit?
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs">
            We're reader-supported. Get the witty headlines in your inbox — free.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 mt-1">
            <button
              onClick={() => setNewsletterOpen(true)}
              data-testid={`ad-fallback-subscribe-${variant}`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-black dark:border-white bg-[#4ECDC4] text-black font-bold text-xs hover:bg-[#3DBDB5] transition-all hard-shadow-hover"
            >
              <Mail className="w-3.5 h-3.5" strokeWidth={2.5} />
              Subscribe
            </button>
            {DONATION_URL && (
              <a
                href={DONATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`ad-fallback-donate-${variant}`}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-black dark:border-white bg-[#FFE66D] text-black font-bold text-xs hover:bg-[#FFD93D] transition-all hard-shadow-hover"
              >
                <Heart className="w-3.5 h-3.5" strokeWidth={2.5} />
                Support us
              </a>
            )}
          </div>
        </div>
        <NewsletterModal isOpen={newsletterOpen} onClose={() => setNewsletterOpen(false)} />
      </>
    );
  }

  // ---- Live AdSense slot ----
  return (
    <div
      data-testid={`ad-slot-${variant}`}
      className={`${cfg.wrapper} ${className}`}
      style={{ minHeight: cfg.minHeight }}
    >
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: 'block', minHeight: cfg.minHeight }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={cfg.slot}
        data-ad-format={cfg.format === 'rectangle' ? 'auto' : cfg.format}
        data-full-width-responsive="true"
      />
    </div>
  );
};

export default AdSlot;
